/* Fiche produit « Clinique douce » (refonte du site, cycle 1, spec docs/superpowers/specs/2026-10-10-refonte-fiche-produit-design.md).
   Chargé en defer après GSAP et ScrollTrigger, sur le gabarit produit seulement (snippets/fp-achat.liquid).
   - Aucune opération de panier : l'ajout passe par <product-form> de Dawn ; ce script écoute seulement ses
     publications (cart-update, variant-change) et relaie le bouton « Acheter maintenant » existant.
   - Un seul moment automatique au défilement : Avant / Avec ; s'y ajoute la vidéo du centre de la section vidéos,
     qui joue sans le son (spec du 13/10/2026), et l'apparition des tuiles de « Ce qui change pour vous » (spec du
     14/10/2026). Le fil des gestes se remplit au défilement ;
     tout le reste répond à un geste. Mouvement réduit respecté ; sans GSAP, la page reste complète.
   - Chaque section s'initialise seule ; dans l'éditeur de thème, réinitialisation au chargement d'une
     section, retour à l'état initial à son retrait, état final affiché (Shopify.designMode). */
(() => {
  const REDUIT = matchMedia('(prefers-reduced-motion: reduce)');
  const ORDI = matchMedia('(min-width: 990px)');
  const enEditeur = () => Boolean(window.Shopify && window.Shopify.designMode);
  /* Dawn déclare PUB_SUB_EVENTS en `const` de premier niveau (assets/constants.js) : une globale, mais pas une propriété
     de window. Le lire par son nom, sinon aucun abonnement ne s'installe (écart É5 du plan du lot 1). */
  const evenements = () => (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS);
  const avecGsap = () => Boolean(window.gsap && window.ScrollTrigger);
  const defile = () => (REDUIT.matches ? 'auto' : 'smooth');
  const hautEnTete = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--fp-haut')) || 0;

  /* Hauteur de l'en-tête s'il colle vraiment (80 puis 70 px au défilement) : cale la galerie, les avis et la carte
     Avant / Avec. L'en-tête ne colle que si son conteneur colle, ou s'il a de la place dans ce conteneur. */
  function poserHauteurEnTete() {
    /* Nouvel en-tête (refonte, cycle 2) : il publie la hauteur qui reste visible quand on descend (assets/entete.css,
       0 sur ordinateur, la ligne compacte sur téléphone). Sans lui, la mesure ci-dessous, inchangée. */
    const publiee = getComputedStyle(document.documentElement).getPropertyValue('--entete-reste').trim();
    if (publiee) {
      document.documentElement.style.setProperty('--fp-haut', publiee);
      return;
    }
    const tete = document.querySelector('.header-v5');
    const conteneur = tete && tete.closest('.shopify-section');
    let hauteur = 0;
    if (tete && conteneur) {
      const colle = getComputedStyle(conteneur).position === 'sticky'
        || (getComputedStyle(tete).position === 'sticky' && conteneur.offsetHeight > tete.offsetHeight + 1);
      if (colle) hauteur = Math.round(tete.getBoundingClientRect().height);
    }
    document.documentElement.style.setProperty('--fp-haut', `${hauteur}px`);
  }

  /* Les dialogues natifs rendent le focus à l'élément qui les a ouverts. */
  function ouvrirDialogue(dialogue) {
    const retour = document.activeElement;
    dialogue.addEventListener('close', () => { if (retour && document.contains(retour)) retour.focus({ preventScroll: true }); }, { once: true });
    dialogue.showModal();
  }
  /* Un toucher hors du panneau ferme le dialogue ; un toucher à l'intérieur, jamais. */
  function fermerAuToucherExterieur(dialogue) {
    dialogue.addEventListener('click', (e) => {
      if (e.target !== dialogue) return;
      const r = dialogue.getBoundingClientRect();
      const dedans = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!dedans || dialogue.matches('.fp-visio')) dialogue.close();
    });
    dialogue.querySelectorAll('[data-fp-fermer]').forEach((b) => b.addEventListener('click', () => dialogue.close()));
  }

  /* Galerie : rail de vignettes, scène, flèches et clavier ; sur mobile la photo suit le glisser (IntersectionObserver). */
  function initGalerie(galerie) {
    const piste = galerie.querySelector('[data-fp-piste]');
    if (!piste) return null;
    const photos = [...piste.children];
    const n = photos.length;
    const vignettes = [...galerie.querySelectorAll('[data-fp-vignette]')];
    const compteur = galerie.querySelector('[data-fp-compteur]');
    let courant = Math.max(0, photos.findIndex((p) => p.classList.contains('fp-on')));
    /* Ordinateur : le rail est sous la photo, comme sur téléphone, et défile quand les vignettes dépassent sa largeur (11
       photos sur le sac à langer) ; fondu à droite tant qu'il en reste à voir. La vignette active est ramenée au centre. */
    const rail = vignettes[0] ? vignettes[0].parentElement : null;
    const railDefile = () => Boolean(rail) && ORDI.matches && rail.scrollWidth > rail.clientWidth + 1;
    function majRail() {
      if (rail) rail.classList.toggle('fp-defile', railDefile());
    }

    function marquer(i) {
      courant = i;
      photos.forEach((p, k) => p.classList.toggle('fp-on', k === i));
      vignettes.forEach((v, k) => {
        v.classList.toggle('fp-on', k === i);
        if (k === i) v.setAttribute('aria-current', 'true'); else v.removeAttribute('aria-current');
      });
      if (compteur) compteur.textContent = `${i + 1} / ${n}`;
      const v = vignettes[i];
      if (v) v.parentElement.scrollTo({ left: v.offsetLeft - v.parentElement.clientWidth / 2 + v.clientWidth / 2, behavior: defile() });
    }
    function aller(i) {
      const cible = (i + n) % n;
      if (!ORDI.matches) piste.scrollTo({ left: cible * piste.clientWidth, behavior: defile() });
      marquer(cible);
    }
    const suivi = new IntersectionObserver((entrees) => {
      if (ORDI.matches) return;
      for (const e of entrees) {
        if (!e.isIntersecting) continue;
        const i = photos.indexOf(e.target);
        if (i !== courant) marquer(i);
      }
    }, { root: piste, threshold: 0.6 });
    photos.forEach((p) => suivi.observe(p));
    vignettes.forEach((v, k) => v.addEventListener('click', () => aller(k)));
    galerie.querySelector('[data-fp-precedente]')?.addEventListener('click', () => aller(courant - 1));
    galerie.querySelector('[data-fp-suivante]')?.addEventListener('click', () => aller(courant + 1));
    galerie.addEventListener('keydown', (e) => {
      if (n < 2) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); aller(courant + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); aller(courant - 1); }
    });
    if (courant > 0 && !ORDI.matches) piste.scrollTo({ left: courant * piste.clientWidth });
    if (rail) {
      /* Fin du rail atteinte (dernière vignette entière) : plus de fondu. IntersectionObserver, jamais d'écouteur de défilement. */
      new IntersectionObserver(([e]) => rail.classList.toggle('fp-fin', e.intersectionRatio > 0.95), { root: rail, threshold: [0, 0.95, 1] }).observe(vignettes[vignettes.length - 1]);
      addEventListener('resize', majRail, { passive: true });
      addEventListener('load', majRail);
      majRail();
    }
    return { aller, courant: () => courant, photos };
  }

  /* Visionneuse plein écran : photo entière, flèches, compteur, glisser ; à la fermeture, la galerie se cale. */
  function initVisionneuse(dialogue, galerie, bouton) {
    if (!dialogue || !galerie || !bouton) return;
    const img = dialogue.querySelector('[data-fp-visio-img]');
    const compteur = dialogue.querySelector('[data-fp-visio-compteur]');
    const n = galerie.photos.length;
    let i = 0;
    function montrer(k) {
      i = (k + n) % n;
      const photo = galerie.photos[i];
      if (compteur) compteur.textContent = `${i + 1} / ${n}`;
      img.classList.add('fp-sort');
      const neuve = new Image();
      neuve.onload = () => { img.src = neuve.src; img.alt = photo.dataset.fpAlt || ''; img.classList.remove('fp-sort'); };
      neuve.src = photo.dataset.fpGrand;
    }
    bouton.addEventListener('click', () => { montrer(galerie.courant()); ouvrirDialogue(dialogue); });
    dialogue.querySelector('[data-fp-visio-precedente]')?.addEventListener('click', () => montrer(i - 1));
    dialogue.querySelector('[data-fp-visio-suivante]')?.addEventListener('click', () => montrer(i + 1));
    dialogue.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') montrer(i + 1);
      if (e.key === 'ArrowLeft') montrer(i - 1);
    });
    let x0 = null;
    dialogue.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
    dialogue.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40 && n > 1) montrer(i + (dx < 0 ? 1 : -1));
    });
    fermerAuToucherExterieur(dialogue);
    dialogue.addEventListener('close', () => galerie.aller(i));
  }

  /* Volet des garanties : chaque case l'ouvre, sa garantie mise en évidence. */
  function initVolet(racine) {
    const volet = racine.querySelector('[data-fp-volet]');
    if (!volet) return;
    const blocs = [...volet.querySelectorAll('[data-fp-garantie]')];
    racine.querySelectorAll('[data-fp-garantie-ouvre]').forEach((b) => b.addEventListener('click', () => {
      const k = b.dataset.fpGarantieOuvre;
      blocs.forEach((x) => x.classList.toggle('fp-focus', x.dataset.fpGarantie === k));
      ouvrirDialogue(volet);
    }));
    fermerAuToucherExterieur(volet);
  }

  /* Accordéons : aria-expanded suit l'état ; la hauteur passe par grid-template-rows (CSS). */
  function initAccordeons(liste) {
    liste.querySelectorAll('.fp-acc__q').forEach((q) => q.addEventListener('click', () => {
      const item = q.closest('.fp-acc__item');
      const ouvert = !item.classList.contains('fp-ouvert');
      item.classList.toggle('fp-ouvert', ouvert);
      q.setAttribute('aria-expanded', String(ouvert));
      if (avecGsap()) setTimeout(() => window.ScrollTrigger.refresh(), 320);
    }));
  }

  /* Avis : filtres selon les données, « Voir les N avis », répartition qui se remplit à l'arrivée. */
  function initAvis(section) {
    const cartes = [...section.querySelectorAll('.fp-avis__carte')];
    const plus = section.querySelector('[data-fp-avis-plus]');
    const filtres = [...section.querySelectorAll('[data-fp-filtre]')];
    filtres.forEach((f) => f.addEventListener('click', () => {
      const v = f.dataset.fpFiltre;
      filtres.forEach((x) => x.setAttribute('aria-pressed', String(x === f)));
      section.classList.toggle('fp-avis--tout', v !== 'tous' || section.classList.contains('fp-avis--deplie'));
      cartes.forEach((c) => { c.hidden = !(v === 'tous' || (v === 'photos' ? c.dataset.photos === 'oui' : c.dataset.note === v)); });
      if (plus) plus.hidden = v !== 'tous' || section.classList.contains('fp-avis--deplie');
    }));
    plus?.addEventListener('click', () => {
      section.classList.add('fp-avis--tout', 'fp-avis--deplie');
      plus.setAttribute('aria-expanded', 'true');
      plus.hidden = true;
    });
    const repartition = section.querySelector('[data-fp-repartition]');
    if (repartition) {
      const vue = new IntersectionObserver((entrees, o) => {
        if (!entrees[0].isIntersecting) return;
        repartition.classList.add('fp-vu');
        o.disconnect();
      }, { threshold: 0.4 });
      vue.observe(repartition);
    }
  }

  /* « Acheter » de la barre et du dernier rappel : relais du bouton « Acheter maintenant » du bloc d'achat. */
  function acheter() {
    const bouton = document.querySelector('.fp-achat [id^="sd-buy-"]');
    if (bouton && !bouton.disabled) bouton.click();
  }
  function ouvrirAlerte() {
    const depliant = document.querySelector('[data-fp-alerte]');
    if (!depliant) return;
    depliant.open = true;
    depliant.scrollIntoView({ behavior: defile(), block: 'center' });
    setTimeout(() => depliant.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 450);
  }

  /* Barre collante : visible quand les boutons du bloc d'achat sont sortis par le haut, cachée dès que le dernier
     rappel entre. Un seul mécanisme, avec ou sans GSAP : IntersectionObserver et une classe CSS (les courbes
     expo.out et power2.in de la spec sont rendues en cubic-bezier dans assets/fiche-produit.css). */
  function initBarre(haut) {
    const barre = haut.querySelector('[data-fp-barre]');
    const boutons = haut.querySelector('[data-fp-achat]');
    if (!barre || !boutons) return;
    let sortis = false;
    let rappelVisible = false;
    const majBarre = () => {
      const visible = sortis && !rappelVisible;
      barre.classList.toggle('fp-on', visible);
      barre.inert = !visible;
      document.body.dataset.fpBarre = visible ? 'on' : 'off';
    };
    new IntersectionObserver(([e]) => { sortis = !e.isIntersecting && e.boundingClientRect.top < 0; majBarre(); }).observe(boutons);
    const rappel = document.querySelector('[data-fp-rappel]');
    if (rappel) new IntersectionObserver(([e]) => { rappelVisible = e.isIntersecting; majBarre(); }).observe(rappel);
  }

  /* Avant / Avec : la photo « avec » balaie la photo « avant » de droite à gauche, texte et étiquette basculent à
     mi-course ; mouvement réduit : un simple fondu. Dans l'éditeur ou sans GSAP : les deux états l'un sous l'autre. */
  function initAvantAvec(section) {
    if (enEditeur() || !avecGsap()) return null;
    const { gsap } = window;
    gsap.registerPlugin(window.ScrollTrigger);
    section.classList.add('fp-vs--anime');
    const avant = section.querySelector('[data-fp-vs-avant]');
    const avec = section.querySelector('[data-fp-vs-avec]');
    const photoAvec = avec.querySelector('.fp-vs__img');
    const sortants = avant.querySelectorAll('.fp-vs__etiquette, .fp-vs__texte');
    const entrants = avec.querySelectorAll('.fp-vs__etiquette, .fp-vs__texte');
    const progres = section.querySelector('[data-fp-vs-progres]');
    const contexte = gsap.context(() => {
      gsap.matchMedia().add({ complet: '(prefers-reduced-motion: no-preference)', reduit: '(prefers-reduced-motion: reduce)' }, ({ conditions }) => {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: section, start: () => `top top+=${hautEnTete() + 8}`, end: 'bottom bottom', scrub: conditions.complet ? 0.35 : true, invalidateOnRefresh: true },
        });
        if (conditions.complet) {
          tl.fromTo(photoAvec, { clipPath: 'inset(0% 0% 0% 100%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none', duration: 1 })
            .fromTo(progres, { scaleX: 0 }, { scaleX: 1, ease: 'none', duration: 1 }, 0)
            .to(sortants, { opacity: 0, y: -10, duration: 0.25, ease: 'power1.out' }, 0.35)
            .fromTo(entrants, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.25, ease: 'power1.out' }, 0.55);
        } else {
          gsap.set(photoAvec, { clipPath: 'inset(0% 0% 0% 0%)', opacity: 0 });
          tl.to(photoAvec, { opacity: 1, duration: 1, ease: 'none' })
            .fromTo(progres, { scaleX: 0 }, { scaleX: 1, ease: 'none', duration: 1 }, 0)
            .to(sortants, { opacity: 0, duration: 0.3 }, 0.4)
            .to(entrants, { opacity: 1, duration: 0.3 }, 0.5);
        }
      });
    }, section);
    return { revert: () => { contexte.revert(); section.classList.remove('fp-vs--anime'); } };
  }

  /* En trois gestes : le fil, plein par défaut, se remplit au défilement quand GSAP prend la main (mouvement complet). */
  function initGestes(section) {
    if (enEditeur() || !avecGsap()) return null;
    const { gsap } = window;
    gsap.registerPlugin(window.ScrollTrigger);
    const rail = section.querySelector('[data-fp-gestes-rail]');
    const liste = section.querySelector('.fp-gestes__liste');
    if (!rail || !liste) return null;
    const contexte = gsap.context(() => {
      gsap.matchMedia().add({ complet: '(prefers-reduced-motion: no-preference)', ordi: '(min-width: 990px)' }, ({ conditions }) => {
        if (!conditions.complet) return;
        const axe = conditions.ordi ? 'scaleX' : 'scaleY';
        gsap.fromTo(rail, { [axe]: 0 }, {
          [axe]: 1,
          ease: 'none',
          scrollTrigger: { trigger: liste, start: 'top 75%', end: conditions.ordi ? 'top 35%' : 'bottom 65%', scrub: 0.4 },
        });
      });
    }, section);
    return { revert: () => contexte.revert() };
  }

  function initHaut(haut) {
    const galerie = initGalerie(haut.querySelector('[data-fp-galerie]'));
    initVisionneuse(haut.querySelector('[data-fp-visionneuse]'), galerie, haut.querySelector('[data-fp-agrandir]'));
    initVolet(haut);
    initBarre(haut);
    const sectionId = haut.dataset.section;
    if (window.subscribe && evenements()) {
      /* Changement de variante (assets/product-info.js) : photo, « Acheter maintenant » et prix de la barre suivent. */
      window.subscribe(evenements().variantChange, ({ data }) => {
        if (!data || data.sectionId !== sectionId || !data.variant) return;
        const v = data.variant;
        const media = v.featured_media && v.featured_media.id;
        const i = galerie && media ? galerie.photos.findIndex((p) => p.dataset.mediaId === String(media)) : -1;
        if (i >= 0) galerie.aller(i);
        const acheterMaintenant = haut.querySelector('[id^="sd-buy-"]');
        if (acheterMaintenant) acheterMaintenant.disabled = !v.available;
        if (window.Shopify && window.Shopify.currency) {
          const montant = new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: window.Shopify.currency.active }).format(v.price / 100);
          haut.querySelectorAll('[data-fp-barre-prix]').forEach((prix) => { prix.textContent = montant; });
          document.querySelectorAll('[data-fp-videos-prix]').forEach((prix) => { prix.textContent = montant; });
        }
        /* « Ajouter » de la section vidéos soumet ce formulaire : il suit la disponibilité du coloris choisi. */
        document.querySelectorAll('[data-fp-videos-ajouter]').forEach((b) => { b.disabled = !v.available; });
      });
    }
    return null;
  }

  /* Vidéos (spec docs/superpowers/specs/2026-10-13-section-videos-fiche-design.md) : coverflow circulaire, la première
     vidéo au centre. Seule la vidéo du centre reçoit sa source, posée à l'approche de l'écran ; elle joue sans le son
     quand la scène est à moitié visible, sans boucle. Pause et son ; toucher le centre l'ouvre en plein écran avec le
     son. Mouvement réduit : rien ne joue seul. video_vue après 3 s de lecture cumulée, une fois par vidéo et par page,
     si les cookies sont acceptés (même condition que pushGtm de assets/chatbot.js). */
  function initVideos(section) {
    const scene = section.querySelector('[data-fp-videos-scene]');
    const cartes = [...section.querySelectorAll('[data-fp-carte]')];
    const n = cartes.length;
    if (!scene || !n) return null;
    const legende = section.querySelector('[data-fp-videos-legende]');
    const annonce = section.querySelector('[data-fp-videos-annonce]');
    const points = [...section.querySelectorAll('[data-fp-videos-point]')];
    const pause = section.querySelector('[data-fp-videos-pause]');
    const son = section.querySelector('[data-fp-videos-son]');
    const visio = section.querySelector('[data-fp-videos-visio]');
    const grand = visio ? visio.querySelector('video') : null;
    const video = (i) => cartes[i].querySelector('video');
    const vues = new Set();
    let c = 0;
    let proche = false;
    let visible = false;
    let arrete = REDUIT.matches;
    let sonActif = false;
    let glisseA = 0;
    let x0 = null;

    function majBoutons() {
      if (pause) {
        pause.dataset.actif = String(arrete);
        pause.setAttribute('aria-label', arrete ? pause.dataset.texteLecture : pause.dataset.textePause);
      }
      if (son) {
        son.dataset.actif = String(sonActif);
        son.setAttribute('aria-label', sonActif ? son.dataset.texteCouper : son.dataset.texteActiver);
      }
    }
    function lire() {
      cartes.forEach((carte, i) => {
        if (i === c) return;
        const autre = video(i);
        if (!autre.paused) autre.pause();
        carte.classList.remove('fp-joue');
      });
      const carte = cartes[c];
      const v = video(c);
      if (!proche) return;
      if (!v.getAttribute('src') && v.dataset.src) v.src = v.dataset.src;
      v.muted = !sonActif;
      if (visible && !arrete && !v.ended && !(visio && visio.open)) {
        v.play().then(() => carte.classList.add('fp-joue')).catch(() => {});
      } else if (!v.paused) {
        v.pause();
      }
    }
    function placer(k, parGeste) {
      c = ((k % n) + n) % n;
      cartes.forEach((carte, i) => {
        let d = (((i - c) % n) + n) % n;
        if (d > Math.floor(n / 2)) d -= n;
        const a = Math.abs(d);
        carte.style.setProperty('--d', String(d));
        carte.style.setProperty('--a', String(a));
        carte.classList.remove('fp-a0', 'fp-a1', 'fp-a2', 'fp-a3');
        carte.classList.add(`fp-a${Math.min(a, 3)}`);
        const voir = carte.querySelector('[data-fp-videos-voir]');
        if (voir) voir.setAttribute('aria-label', i === c ? voir.dataset.texteCentre : voir.dataset.texteVoisine);
      });
      points.forEach((p, i) => p.classList.toggle('fp-on', i === c));
      const texte = cartes[c].dataset.legende || '';
      if (legende) legende.textContent = texte;
      if (parGeste) {
        if (video(c).ended) video(c).currentTime = 0;
        if (annonce) annonce.textContent = [cartes[c].getAttribute('aria-label'), texte].filter(Boolean).join(' : ');
        if (document.activeElement && document.activeElement.closest('[data-fp-carte]')) {
          cartes[c].querySelector('[data-fp-videos-voir]').focus({ preventScroll: true });
        }
      }
      lire();
    }
    function pleinEcran() {
      if (!visio || !grand) return;
      const v = video(c);
      const debut = v.currentTime || 0;
      v.pause();
      grand.src = v.dataset.src;
      grand.muted = false;
      grand.addEventListener('loadedmetadata', () => { if (debut < grand.duration - 0.5) grand.currentTime = debut; }, { once: true });
      ouvrirDialogue(visio);
      grand.play().catch(() => {});
    }
    const consenti = () => {
      try { return localStorage.getItem('sd_cookie_consent') === 'accepted'; } catch (err) { return false; }
    };
    function suivre(v, indice) {
      let dernier = 0;
      let cumul = 0;
      v.addEventListener('emptied', () => { dernier = 0; cumul = 0; });
      v.addEventListener('timeupdate', () => {
        const t = v.currentTime;
        if (t > dernier && t - dernier < 1) cumul += t - dernier;
        dernier = t;
        const i = indice();
        if (cumul < 3 || vues.has(i)) return;
        vues.add(i);
        if (!consenti()) return;
        (window.dataLayer = window.dataLayer || []).push({ event: 'video_vue', video_provenance: cartes[i].dataset.provenance, video_position: i + 1, product_handle: section.dataset.produit });
      });
    }

    section.addEventListener('click', (e) => {
      if (performance.now() - glisseA < 400) return;
      const voir = e.target.closest('[data-fp-videos-voir]');
      if (voir) {
        const i = cartes.indexOf(voir.closest('[data-fp-carte]'));
        if (i === c) pleinEcran(); else placer(i, true);
      } else if (e.target.closest('[data-fp-videos-precedente]')) {
        placer(c - 1, true);
      } else if (e.target.closest('[data-fp-videos-suivante]')) {
        placer(c + 1, true);
      } else if (e.target.closest('[data-fp-videos-pause]')) {
        arrete = !arrete;
        if (!arrete && video(c).ended) video(c).currentTime = 0;
        majBoutons();
        lire();
      } else if (e.target.closest('[data-fp-videos-son]')) {
        sonActif = !sonActif;
        majBoutons();
        video(c).muted = !sonActif;
      }
    });
    scene.addEventListener('pointerdown', (e) => { x0 = e.clientX; });
    scene.addEventListener('pointercancel', () => { x0 = null; });
    scene.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40 && n > 1) {
        glisseA = performance.now();
        placer(c + (dx < 0 ? 1 : -1), true);
      }
    });
    section.addEventListener('keydown', (e) => {
      if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || n < 2) return;
      if (!e.target.closest('[data-fp-videos-scene], .fp-videos__nav')) return;
      e.preventDefault();
      placer(c + (e.key === 'ArrowRight' ? 1 : -1), true);
    });
    if (visio) {
      fermerAuToucherExterieur(visio);
      visio.addEventListener('close', () => {
        grand.pause();
        grand.removeAttribute('src');
        grand.load();
        lire();
      });
    }
    cartes.forEach((carte, i) => suivre(video(i), () => i));
    if (grand) suivre(grand, () => c);
    const approche = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      proche = true;
      approche.disconnect();
      lire();
    }, { rootMargin: '400px 0px' });
    approche.observe(section);
    const vue = new IntersectionObserver(([e]) => { visible = e.isIntersecting; lire(); }, { threshold: 0.5 });
    vue.observe(scene);
    majBoutons();
    placer(0, false);
    return {
      revert() {
        approche.disconnect();
        vue.disconnect();
        cartes.forEach((carte, i) => video(i).pause());
      },
    };
  }

  /* « Ce qui change pour vous » (spec du 14/10/2026) : chaque tuile apparaît une fois, à l'arrivée à l'écran. Les tuiles
     déjà à l'écran restent visibles ; les autres ne sont cachées qu'une fois data-fp-anime posé (fiche-produit.css) :
     sans script, en mouvement réduit ou dans l'éditeur de thème, rien n'est caché. */
  function initBenefices(section) {
    if (REDUIT.matches || enEditeur() || !('IntersectionObserver' in window)) return null;
    const tuiles = [...section.querySelectorAll('.fp-tuile')];
    tuiles.forEach((t) => { if (t.getBoundingClientRect().top < window.innerHeight) t.classList.add('fp-vu'); });
    section.setAttribute('data-fp-anime', '');
    const vue = new IntersectionObserver((entrees) => entrees.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('fp-vu');
      vue.unobserve(e.target);
    }), { threshold: 0.2 });
    tuiles.filter((t) => !t.classList.contains('fp-vu')).forEach((t) => vue.observe(t));
    return {
      revert() {
        vue.disconnect();
        section.removeAttribute('data-fp-anime');
        tuiles.forEach((t) => t.classList.remove('fp-vu'));
      },
    };
  }

  const INITS = [
    ['[data-fp-haut]', initHaut],
    ['[data-fp-accordeons]', (el) => { initAccordeons(el); return null; }],
    ['[data-fp-avant-avec]', initAvantAvec],
    ['[data-fp-gestes]', initGestes],
    ['[data-fp-avis]', (el) => { initAvis(el); return null; }],
    ['[data-fp-videos]', initVideos],
    ['[data-fp-benefices]', initBenefices],
  ];
  const instances = new Map();
  function initDans(racine) {
    for (const [selecteur, init] of INITS) {
      racine.querySelectorAll(selecteur).forEach((el) => {
        if (instances.has(el)) return;
        instances.set(el, init(el));
      });
    }
  }
  function revertDans(racine) {
    for (const [el, instance] of instances) {
      if (!racine.contains(el)) continue;
      instance?.revert?.();
      instances.delete(el);
    }
  }
  const rafraichir = () => { if (avecGsap()) window.ScrollTrigger.refresh(); };

  /* Ligne « Marque française » sous la capsule (écart É3 du plan du 14/10/2026) : entre elle et la section visée, les
     sections encore non rendues comptaient pour leur hauteur estimée, et l'ancre arrivait jusqu'à 270 px à côté. Au
     clic, avant la navigation par ancre du navigateur (aucun preventDefault), elles sont rendues (.fp-ancre-rendue,
     fiche-produit.css) et la mise en page est lue : le défilement part des vraies hauteurs. */
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('.fp-marque-lien')) return;
    document.documentElement.classList.add('fp-ancre-rendue');
    void document.body.offsetHeight;
  }, true);

  /* Confirmations : « Ajouté au panier » sur le bouton qui a ajouté, annonce aux lecteurs d'écran, pastille du panier. */
  if (window.subscribe && evenements()) {
    window.subscribe(evenements().cartUpdate, (evt) => {
      if (!evt || evt.source !== 'product-form') return;
      const id = String(evt.productVariantId);
      document.querySelectorAll('.fp form[data-type="add-to-cart-form"], .fp .fp-carte__f').forEach((form) => {
        const champ = form.querySelector('input[name="id"]');
        const bouton = form.querySelector('button[name="add"]');
        if (!champ || !bouton || champ.value !== id) return;
        bouton.classList.add('fp-ajoute');
        if (bouton.id && bouton.id.startsWith('ProductSubmitButton-')) {
          clearTimeout(bouton.fpMinuteur);
          bouton.fpMinuteur = setTimeout(() => bouton.classList.remove('fp-ajoute'), 1800);
          const annonce = form.querySelector('[data-fp-annonce]');
          if (annonce) { annonce.textContent = ''; requestAnimationFrame(() => { annonce.textContent = annonce.dataset.texte || ''; }); }
          document.querySelectorAll(`[data-fp-videos-ajouter][form="${form.id}"]`).forEach((b) => {
            b.classList.add('fp-ajoute');
            clearTimeout(b.fpMinuteur);
            b.fpMinuteur = setTimeout(() => b.classList.remove('fp-ajoute'), 1800);
          });
        }
      });
      const bulle = document.getElementById('cart-icon-bubble');
      if (bulle) {
        bulle.classList.remove('fp-pop');
        void bulle.offsetWidth;
        bulle.classList.add('fp-pop');
        setTimeout(() => bulle.classList.remove('fp-pop'), 700);
      }
    });
  }

  /* Relais : « Acheter », alerte de retour en stock, question (assistant s'il est rendu, sinon page contact). */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-fp-barre-achat], [data-fp-rappel-achat]')) { acheter(); return; }
    if (e.target.closest('[data-fp-barre-alerte]')) { ouvrirAlerte(); return; }
    const question = e.target.closest('[data-fp-question]');
    const bulleAssistant = document.querySelector('#sd-chat .sd-chat__fab');
    if (question && bulleAssistant) { e.preventDefault(); bulleAssistant.click(); }
  });

  poserHauteurEnTete();
  new MutationObserver(poserHauteurEnTete).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', poserHauteurEnTete, { passive: true });
  initDans(document);
  window.addEventListener('load', rafraichir);
  document.fonts?.ready.then(rafraichir);
  document.addEventListener('shopify:section:load', (e) => { initDans(e.target); rafraichir(); });
  document.addEventListener('shopify:section:unload', (e) => revertDans(e.target));
})();
